<script setup lang="ts">
const { visitorOrUserCountry } = useParameters();

const { isCentralizedExclusionEnabled } = useFeatureFlagsJson();

const {
  linkToAppDownload,
  linkToHelpCenter,
  linkToBlog,
  linkToAffiliates,
  linkToAffiliatesLabel,
  linkToTelegram,
  linkToTelegramLabel,
  finalLinkToPromotions,
  linkToPromotionsTargetBlank,
  linkToResponsibleGaming,
  linkToCentralizedExclusion,
} = useLinks();

const {
  isSportsHomePage,
  goToSportBookPage,
  goToSportsHomePage,
  isSportBookPageActive,
} = useSportsBook();

const { userCanOnlyWithdraw } = useUserLimits();

const termsStore = useTermsStore();

const termsLinks = computed<Array<{ route: string; title: string }>>(() => {
  return termsStore.termsLinks;
});

const hasLoadedTerms = ref(false);

const loadTermsLazily = async () => {
  if (!hasLoadedTerms.value && !termsLinks.value.length) {
    hasLoadedTerms.value = true;
    await termsStore.getCurrentLangTermsPlusDefaultTerms('pt-br');
  }
};

// Load terms in background when component mounts (non-blocking)
onMounted(() => {
  nextTick(() => {
    loadTermsLazily();
  });
});

const paymentIconName = computed(() => {
  if (visitorOrUserCountry.value === 'MEX') {
    return 'payments/spei';
  }

  return 'payments/pix';
});

const { isPokerSectionEnabled } = usePoker();
</script>

<template>
  <div class="info-section-grid">
    <div class="xl:flex flex-col hidden">
      <BaseCommonLogo
        allow-redirect
        always-logo
        class="h-8 min-w-full lg:min-w-[120px] justify-center lg:justify-start max-w-[120px] !mr-0"
        custom-size
      />
      <div class="flex mt-4 md:mt-6">
        <FooterSocial :show-title="false" />
      </div>
    </div>

    <div
      v-if="!userCanOnlyWithdraw"
      class="flex flex-col"
    >
      <b class="title">{{ $t('footer.bet') }}</b>

      <div class="flex flex-col menu-items">
        <button
          :class="{
            active: isSportsHomePage,
          }"
          @click="goToSportsHomePage()"
        >
          {{ $t('footer.sports_bet') }}
        </button>

        <button
          :class="{
            active: isSportBookPageActive('live'),
          }"
          @click="goToSportBookPage('live')"
        >
          {{ $t('footer.live_sports') }}
        </button>

        <NuxtLink
          active-class="opacity-100"
          no-prefetch
          to="/games/category/slots/popular"
        >
          {{ $t('sidebar.slot_games') }}
        </NuxtLink>

        <NuxtLink
          active-class="opacity-100"
          no-prefetch
          to="/games/category/cassino-ao-vivo/popular"
        >
          {{ $t('sidebar.live_games') }}
        </NuxtLink>

        <NuxtLink
          v-if="isPokerSectionEnabled"
          active-class="opacity-100"
          no-prefetch
          to="/poker"
        >
          {{ $t('sidebar.poker_live') }}
        </NuxtLink>
      </div>
    </div>

    <div class="flex flex-col">
      <b class="title">{{ $t('footer.community') }}</b>

      <div class="flex flex-col menu-items">
        <NuxtLink
          v-if="finalLinkToPromotions && !userCanOnlyWithdraw"
          :external="linkToPromotionsTargetBlank"
          no-prefetch
          :target="linkToPromotionsTargetBlank ? '_blank' : undefined"
          :to="finalLinkToPromotions"
        >
          <span>{{ $t('sidebar.promotions') }}</span>
        </NuxtLink>

        <NuxtLink
          v-if="linkToBlog"
          active-class="opacity-100"
          no-prefetch
          target="_blank"
          :to="linkToBlog"
        >
          {{ $t('sidebar.blog') }}
        </NuxtLink>

        <NuxtLink
          v-if="linkToHelpCenter"
          active-class="opacity-100"
          no-prefetch
          target="_blank"
          :to="linkToHelpCenter"
        >
          <span>{{ $t('sidebar.help_center') }}</span>
        </NuxtLink>

        <NuxtLink
          active-class="opacity-100"
          no-prefetch
          :to="localeLink('/help')"
        >
          <span>{{ $t('sidebar.service_channels') }}</span>
        </NuxtLink>

        <NuxtLink
          v-if="linkToAffiliates && !userCanOnlyWithdraw"
          external
          no-prefetch
          target="_blank"
          :to="linkToAffiliates"
        >
          <span>
            {{
              linkToAffiliatesLabel
                ? $t(linkToAffiliatesLabel)
                : $t('sidebar.become_affiliate')
            }}
          </span>
        </NuxtLink>

        <NuxtLink
          v-if="linkToAppDownload && !userCanOnlyWithdraw"
          active-class="opacity-100"
          external
          no-prefetch
          target="_blank"
          :to="linkToAppDownload"
        >
          {{ $t('footer.download_apk') }}
        </NuxtLink>

        <NuxtLink
          v-if="linkToTelegram"
          no-prefetch
          target="_blank"
          :to="linkToTelegram"
        >
          <span>
            {{
              linkToTelegramLabel
                ? $t(linkToTelegramLabel)
                : $t('sidebar.access_telegram')
            }}
          </span>
        </NuxtLink>
      </div>
    </div>

    <div
      v-if="termsLinks.length"
      class="flex flex-col"
      @mouseenter="loadTermsLazily"
    >
      <b class="title">{{ $t('footer.rules') }}</b>

      <div class="flex flex-col menu-items">
        <RouterLink
          v-for="link in termsLinks"
          :key="link.route"
          active-class="opacity-100"
          class="menuLinks"
          :to="localeLink(link.route)"
        >
          {{ $t(link.title) }}
        </RouterLink>

        <NuxtLink
          v-if="linkToResponsibleGaming"
          external
          target="_blank"
          :to="linkToResponsibleGaming"
        >
          {{ $t('terms.responsible_gaming') }}
        </NuxtLink>

        <NuxtLink
          v-if="isCentralizedExclusionEnabled"
          external
          target="_blank"
          :to="linkToCentralizedExclusion"
        >
          <span>{{ $t('footer.self_exclusion') }}</span>
        </NuxtLink>
      </div>
    </div>

    <div class="flex flex-col">
      <b class="title">
        {{ $t('footer.useful_links') }}
      </b>

      <div class="flex flex-col items-start gap-3">
        <NuxtLink
          class="contactButton"
          to="/contact"
        >
          <NuxtIcon
            class="buttonIcon"
            name="chat"
          />
          {{ $t('contact.title') }}
        </NuxtLink>
      </div>
    </div>

    <div class="flex flex-col">
      <b class="title">{{ $t('footer.payment') }}</b>

      <div class="flex flex-col menu-items paymentItems">
        <NuxtIcon
          class="h-6 w-full"
          filled
          :name="paymentIconName"
        />
      </div>
    </div>
  </div>

  <div class="info-section-socials">
    <BaseCommonLogo
      allow-redirect
      always-logo
      class="h-8 min-w-full lg:min-w-[120px] justify-center lg:justify-start max-w-[120px] !mr-0"
      custom-size
    />
    <div class="flex mt-4">
      <FooterSocial :show-title="false" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.info-section-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 20px;

  @media (max-width: 1279px) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @apply pb-6 pt-2 md:pt-8 md:pb-8 px-1 md:px-2 relative;

  &:after {
    @apply w-full h-px bg-footer-texts absolute top-full left-0 opacity-15;
    content: '';
  }

  .contactButton {
    @apply inline-flex items-center justify-start gap-2 py-2 px-3 text-xs xl:text-sm font-bold leading-4 text-texts/90 hover:opacity-100 bg-black/30 rounded-lg;

    &:hover {
      @apply bg-black/40;
    }

    .buttonIcon {
      @apply text-xl text-primary;
    }
  }
}

.info-section-socials {
  @apply relative pt-5 flex flex-col xl:hidden items-center;

  &:after {
    @apply w-full h-px bg-footer-texts absolute top-full left-0 opacity-15;
    content: '';
  }
}

.title {
  @apply text-white text-sm mb-4;
}

.menu-items {
  @apply text-white text-sm gap-1 opacity-60;
  a,
  button {
    @apply cursor-pointer text-left;
  }
}
</style>

<style>
/* verificar pq não funcionou dentro do scoped */
.paymentItems {
  @apply w-full;
  svg {
    @apply w-auto h-6 min-h-6;
  }
}
</style>
